import {
  Flex,
  Button,
  Box,
  Stack,
  Alert,
  AlertDescription,
} from '@chakra-ui/react';
import {
  MerchantTeaserFragmentFragment,
  ParagraphMerchantListFragmentFragment,
} from '../../../generated/types';
import Container from '../../Layout/Container';
import Wrapper from '../Wrapper';
import React, { FormEvent, useEffect, useState } from 'react';
import { SearchIcon } from '@chakra-ui/icons';
import SidebarWrapper from '../../Layout/SidebarWrapper';
import useIsMobile from '../../../hooks/useIsMobile';
import PlaceAutocomplete, {
  TransformedGeocoderFeature,
} from '../../Common/PlaceAutocomplete';
import dynamic from 'next/dynamic';
import MerchantTeaser from '../../Node/Merchant/MerchantTeaser';

const MerchantMap = dynamic(() => import('../../Common/MerchantMap'), {
  ssr: false,
});

interface MerchantListProps {
  data: ParagraphMerchantListFragmentFragment;
}

export interface Bounds {
  maxLat: number;
  maxLng: number;
  minLat: number;
  minLng: number;
}

const MerchantList: React.FC<MerchantListProps> = ({ data }) => {
  const isMobile = useIsMobile();
  const [query, setQuery] = useState('');
  const [showError, setShowError] = useState(false);
  const [
    activeMerchant,
    setActiveMerchant,
  ] = useState<MerchantTeaserFragmentFragment | null>(null);
  const [
    selectedItem,
    setSelectedItem,
  ] = useState<TransformedGeocoderFeature>();
  const [currentCenter, setCurrentCenter] = useState<null | [number, number]>(
    null
  );
  const [currentBounds, setCurrentBounds] = useState<null | Bounds>(null);
  const [filteredMerchants, setFilteredMerchants] = useState<
    MerchantTeaserFragmentFragment[]
  >(data.merchants?.items);

  const onSelectItem = (item: TransformedGeocoderFeature) => {
    setSelectedItem(item);
    setShowError(false);
  };

  const onChangeQuery = (text: string) => setQuery(text);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!selectedItem) {
      setShowError(true);
    } else {
      setCurrentCenter([selectedItem.position[1], selectedItem.position[0]]);
    }
  };

  const onClickShowOnMapTeaser = (data: MerchantTeaserFragmentFragment) => {
    if (data.location && data.location.lat && data.location.lng) {
      setActiveMerchant(data);
      setCurrentCenter([data.location.lat, data.location.lng]);
    }
  };

  useEffect(() => {
    if (currentBounds) {
      setFilteredMerchants(
        data.merchants?.items.filter((merchant) => {
          return (
            merchant.location &&
            merchant.location.lng >= currentBounds.minLng &&
            merchant.location.lng <= currentBounds.maxLng &&
            merchant.location.lat >= currentBounds.minLat &&
            merchant.location.lat <= currentBounds.maxLat
          );
        })
      );
    }
  }, [currentBounds]);

  return (
    <Wrapper className="ParagraphMerchantList">
      <Container>
        <form onSubmit={onSubmit}>
          <Stack isInline={!isMobile} pos="relative" spacing={3}>
            <PlaceAutocomplete
              id={`place-autocomplete-haendler`}
              placeholder="Wo suchst Du einen Händler?"
              onSelectItem={onSelectItem}
              onChangeQuery={onChangeQuery}
            />
            <Box flex={isMobile ? '1' : '0'}>
              <Button
                width="100%"
                variant="outline"
                colorScheme="brand"
                type="submit"
              >
                <SearchIcon marginRight={2} />
                Suchen
              </Button>
            </Box>
          </Stack>
          {showError && (
            <Alert status="error" marginTop={2}>
              <AlertDescription>
                {query !== ''
                  ? 'Bitte wähle einen vorgeschlagenen Eintrag aus der Liste oder verwende Deinen aktuellen Standort.'
                  : 'Du musst einen Ort angeben.'}
              </AlertDescription>
            </Alert>
          )}
        </form>
        <Flex
          flexDirection={{
            base: 'column',
            md: 'column',
            lg: 'row',
          }}
          marginTop={4}
        >
          <Box overflow="hidden" flex="1" p="3" pl="0">
            <MerchantMap
              center={currentCenter}
              markers={data.merchants!}
              activeMerchant={activeMerchant}
              onBoundsChange={setCurrentBounds}
            />
          </Box>
          <SidebarWrapper>
            <Stack
              maxHeight={isMobile ? '400px' : '60vh'}
              overflowY="auto"
              spacing={4}
              p={1}
              divider={<Box></Box>}
            >
              {filteredMerchants.map((merchant, i) => {
                if (!merchant) return null;

                return (
                  <MerchantTeaser
                    key={merchant.id}
                    number={i + 1}
                    data={merchant}
                    onClickShowOnMap={onClickShowOnMapTeaser}
                  />
                );
              })}
            </Stack>
          </SidebarWrapper>
        </Flex>
      </Container>
    </Wrapper>
  );
};

export default MerchantList;
